import React, { useEffect, useMemo, useState } from 'react'
import { Card, Checkbox } from '@sede-enterprise/shell-ds-react-framework';
import DetailsTopContributors from '../../components/DetailsTopContributors/DetailsTopContributors';
import { generateCheckboxConfig } from '../../util/checkboxConfig';
import { INTERVAL_OPTIONS } from '../../util/constants';
import { calculateFixedYRange, generateRequests, getSiteType } from '../../util/helperFunctions';
import useUpdateChart from '../../util/hooks/useUpdateChart';
import MetricChart from '../../components/MetricChart/MetricChart';

const CLDetailsChart = (props) => {
    const {
        units,
        prodUnit,
        corrosionLoopID,
        site,
        isRealSensor,
        metricData,
        loading,
        start,
        setStart,
        end,
        setEnd,
        interval,
        setSelectedInterval,
        setRequestParams,
        formDirty,
        refreshChartData
    } = props;

    const { isNorco, isNorcoWest, isRhienland, isSyngas, isBonga } = getSiteType(site, prodUnit);
    const [clCheckboxes, setClCheckboxes] = useState(
        generateCheckboxConfig({ units, site, prodUnit, isNorco, isNorcoWest, isRhienland, isSyngas, isBonga })
    );

    useEffect(() => {
        const updatedCheckboxConfig = generateCheckboxConfig({ units, site, prodUnit, isNorco, isNorcoWest, isRhienland, isSyngas, isBonga });

        setClCheckboxes((prevCheckboxes) => {
            const mergedCheckboxes = Object.keys(updatedCheckboxConfig).reduce((acc, key) => {
                acc[key] = {
                    ...updatedCheckboxConfig[key],
                    enabled: prevCheckboxes[key]?.enabled ?? updatedCheckboxConfig[key].enabled, // Preserve 'enabled' if it exists
                };
                return acc;
            }, {});

            return {
                ...mergedCheckboxes,
                "MissingFeatures": {
                    ...mergedCheckboxes.MissingFeatures,
                    available: false,
                },
                "ForecastWallThickness": {
                    ...mergedCheckboxes.ForecastWallThickness,
                    available: false,
                },
                "MinimumAllowableWallThickness": {
                    ...mergedCheckboxes.MinimumAllowableWallThickness,
                    available: false,
                },
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [units, site, prodUnit, isNorco, isNorcoWest, isRhienland, isSyngas, isBonga]);

    const clRequests = useMemo(() => {
        return generateRequests(clCheckboxes, corrosionLoopID, "CLExpression");
    }, [clCheckboxes, corrosionLoopID]);

    useEffect(() => {
        if (
            !!clRequests.length
        ) {
            refreshChartData([
                ...clRequests,
            ]);
        } else {
            refreshChartData([]);
        }
    }, [
        clRequests,
        refreshChartData
    ]);

    const clCheckboxClicked = (id) => {
        if (id === "PredictedCorrosionRateConfidenceBandLowerValue") {
            setClCheckboxes({
                ...clCheckboxes,
                "PredictedCorrosionRateConfidenceBandUpperValue": {
                    ...clCheckboxes.PredictedCorrosionRateConfidenceBandUpperValue,
                    enabled: !clCheckboxes.PredictedCorrosionRateConfidenceBandUpperValue.enabled,
                },
                "PredictedCorrosionRateConfidenceBandLowerValue": {
                    ...clCheckboxes.PredictedCorrosionRateConfidenceBandLowerValue,
                    enabled: !clCheckboxes.PredictedCorrosionRateConfidenceBandLowerValue.enabled,
                }
            });
        }
        else {
            setClCheckboxes({
                ...clCheckboxes,
                [id]: {
                    ...clCheckboxes[id],
                    enabled: !clCheckboxes[id].enabled,
                },
            });
        }
    };

    const updateChart = useUpdateChart({
        setRequestParams,
        start,
        end,
        interval,
    });

    const fixedYRange = () => {
        const fixedYRangeVariables = [
            'Measured wall thickness',
            'Predicted wall thickness',
            'Measured corrosion rate',
            'Predicted corrosion rate',
            'Predicted corrosion rate confidence band',
        ];
        return calculateFixedYRange(metricData, units, fixedYRangeVariables);
    };
    return (
        <div className='features-container'>

            <Card className='details-chart-card' bodyPadding={false}>
                <div className='graphs-container'>
                    <MetricChart
                        yaxes={{
                            yaxis: { fixedrange: false, side: "left" },
                            yaxis2: {
                                fixedrange: false,
                                side: "right",
                                overlaying: "y",
                                anchor: "x",
                            },
                        }}
                        data={metricData}
                        metricData={metricData}
                        corrosionLoopID={corrosionLoopID}
                        loading={loading}
                        start={start}
                        setStart={setStart}
                        end={end}
                        setEnd={setEnd}
                        interval={interval}
                        fixedYRange={fixedYRange()}
                        setSelectedInterval={setSelectedInterval}
                        intervalOpts={INTERVAL_OPTIONS}
                        formDirty={formDirty}
                        onSubmit={updateChart}
                    />
                </div>
                <div className='metric-chart-checkboxes'>
                    <div className='metric-checkboxes-row'>
                        <p className='data-label'>Features</p>
                        <div className='checkboxes-container'>
                            {Object.entries(clCheckboxes)
                                .filter(([key, value]) => value.group === 1 && value.available)
                                .map(k => k[0])
                                .map((key) => {
                                    return (
                                        <Checkbox
                                            className='checkbox-item'
                                            key={key}
                                            label={clCheckboxes[key].name + " " + clCheckboxes[key].unit}
                                            checked={clCheckboxes[key].enabled}
                                            onChange={() => clCheckboxClicked(key)}
                                        />
                                    );
                                })}
                        </div>
                    </div>
                    <div className='metric-checkboxes-row'>
                        <p className='data-label'> Features (Second Y-axis)</p>
                        <div className='checkboxes-container'>
                            {Object.entries(clCheckboxes)
                                .filter(([key, value]) => value.group === 2 && value.available)
                                .map(k => k[0])
                                .map(
                                    (key) => {
                                        return (
                                            <Checkbox
                                                className='checkbox-item'
                                                key={key}
                                                label={
                                                    clCheckboxes[key].name + " " + clCheckboxes[key].unit
                                                }
                                                checked={
                                                    clCheckboxes[key].enabled
                                                }
                                                onChange={() =>
                                                    clCheckboxClicked(key)
                                                }
                                            />
                                        );
                                    })}
                        </div>
                    </div>
                    <div className='metric-checkboxes-row'>
                        <p className='data-label'>Predictions</p>
                        <div className='checkboxes-container'>
                            {Object.entries(clCheckboxes)
                                .filter(([key, value]) => value.group === 3 && value.available)
                                .map(k => k[0])
                                .map((key) => {
                                    return (
                                        <Checkbox
                                            className='checkbox-item'
                                            key={key}
                                            label={
                                                clCheckboxes[key].name.replace(
                                                    "Predicted corrosion rate confidence band lower value",
                                                    "Predicted corrosion rate confidence band"
                                                ) +
                                                " " +
                                                clCheckboxes[key].unit
                                            }
                                            checked={clCheckboxes[key].enabled}
                                            onChange={() =>
                                                clCheckboxClicked(key)
                                            }
                                        />
                                    );
                                })}
                        </div>
                    </div>
                </div>
            </Card>
            <br />
            <br />
            <DetailsTopContributors
                units={units}
                pathPrefix="CL"
                corrosionLocationID={corrosionLoopID}
                start={start}
                end={end}
                isRealSensor={isRealSensor}
            />
        </div>

    )
}

export default CLDetailsChart